import type { SalesOrdersTranslations } from './types';

/**
 * Arabic translations for SalesOrders domain
 * Comprehensive translations covering all UI text
 */
export const arTranslations: SalesOrdersTranslations = {
  // Page/Section level
  title: 'طلبات المبيعات',
  subtitle: 'إدارة طلبات المبيعات والعقود',
  overview: 'نظرة عامة',

  // Actions
  addSalesOrder: 'إضافة طلب مبيعات',
  editSalesOrder: 'تعديل طلب المبيعات',
  deleteSalesOrder: 'حذف طلب المبيعات',
  createSalesOrder: 'إنشاء طلب مبيعات',
  customer: 'العميل',
  selectCustomerToCreate:
    'يرجى اختيار عميل من رأس الصفحة قبل إنشاء طلب مبيعات.',
  readOnlyAccess: 'صلاحيتك تتيح لك العرض فقط لطلبات المبيعات.',
  newSalesOrder: 'طلب مبيعات جديد',
  reorder: 'إعادة الطلب',
  reorderTitle: 'إعادة طلب المبيعات',
  reorderFrom: 'إعادة الطلب من {{orderNumber}}',
  placeReorder: 'تنفيذ إعادة الطلب',
  renew: 'تجديد',
  cancel: 'إلغاء',
  generatedOrdersAlert: 'عدد الطلبات المُولدة: {{count}}',
  confirmCreateSalesOrderTitle: 'تأكيد أمر البيع',
  confirmCreateSalesOrderDescription:
    'راجع طلبات التنفيذ المُولدة قبل إنشاء أمر البيع.',
  generatedOrders: 'الطلبات المُولدة',
  requestedQuantity: 'المطلوب',
  droppedQuantity: 'المستبعد',
  dropped: 'مستبعد',
  droppedOrdersSummary: 'تم استبعاد {{count}}',
  details: 'التفاصيل',
  downloadPDF: 'تحميل PDF',
  viewOrders: 'عرض الطلبات',
  fillDetails: 'أدخل التفاصيل أدناه',

  // Stats/Status labels
  draft: 'مسودة',
  active: 'نشط',
  expiring: 'ينتهي قريباً',
  completed: 'مكتمل',
  total: 'الإجمالي',
  totalValue: 'القيمة الإجمالية',
  expiringSoon: 'ينتهي قريباً',
  expires: 'ينتهي',
  value: 'القيمة',
  consumption: 'الاستهلاك',
  used: 'المستخدم',
  orders: 'الطلبات',

  // Search and filters
  searchPlaceholder: 'البحث في طلبات المبيعات...',
  noSalesOrders: 'لا توجد طلبات مبيعات',
  noOrdersDescription:
    'لم تقم بإنشاء أي طلبات مبيعات بعد. أنشئ طلبك الأول للبدء.',
  createFirst: 'أنشئ طلب المبيعات الأول',
  noItemsFound: 'لم يتم العثور على طلبات مبيعات',
  noItemsDescription: 'لم تقم بإضافة أي طلبات مبيعات بعد.',
  addFirst: 'أضف طلب المبيعات الأول',
  filters: 'التصفية',
  filtersTitle: 'تصفية طلبات المبيعات',
  filterByStatus: 'التصفية حسب الحالة',
  filterByDeliveryAddress: 'التصفية حسب عنوان التسليم',
  filterByProduct: 'التصفية حسب المنتج',
  filterByValue: 'التصفية حسب القيمة',
  filterByMinValue: 'الحد الأدنى للقيمة',
  filterByMaxValue: 'الحد الأقصى للقيمة',
  filterByCreatedDate: 'التصفية حسب تاريخ الإنشاء',
  filterByValidityDate: 'التصفية حسب تاريخ الصلاحية',
  filterByAddress: 'التصفية حسب العنوان',
  filterByFactory: 'التصفية حسب المصنع',
  noDeliveryAddresses: 'لا توجد عناوين تسليم متاحة',
  noProductsAvailable: 'لا توجد منتجات متاحة',
  noFactoriesAvailable: 'لا توجد مصانع متاحة',
  noFactories: 'لا توجد مصانع متاحة',
  loadingFactories: 'جارٍ تحميل المصانع المتاحة...',
  factoryRequired: 'يرجى اختيار المصنع للمتابعة',
  searchFactories: 'البحث عن المصانع...',
  sorting: 'الترتيب',
  sortBy: 'ترتيب حسب',
  sortOrder: 'اتجاه الترتيب',
  searchProducts: 'البحث عن المنتجات...',
  searchAddresses: 'البحث عن العناوين...',
  selectCreatedDateRange: 'اختر نطاق تاريخ الإنشاء',
  selectValidityDateRange: 'اختر نطاق تاريخ الصلاحية',

  // Form fields
  basicInfo: 'المعلومات الأساسية',
  startDate: 'تاريخ البدء',
  endDate: 'تاريخ الانتهاء',
  paymentMethod: 'طريقة الدفع',
  city: 'المدينة',
  selectCity: 'اختر المدينة',
  searchCity: 'ابحث عن مدينة...',
  noCityFound: 'لا توجد مدينة',
  loadingCities: 'جارٍ تحميل المدن المتاحة...',
  noCitiesForFactory: 'لا توجد مدن تسليم مهيأة لهذا المصنع',
  cityRequired: 'المدينة مطلوبة',
  selectCityFirst: 'اختر المدينة أولاً',
  deliveryAddress: 'عنوان التسليم',
  selectedAddress: 'العنوان المحدد',
  selectDeliveryAddress: 'اختر عنوان التسليم',
  deliveryAddressRequired: 'عنوان التسليم مطلوب',
  deliveryAddressOptional: 'عنوان التسليم اختياري حالياً',
  orderLines: 'بنود الطلب',
  productType: 'نوع المنتج',
  splitBy: 'تقسيم حسب',
  qtyPerOrder: 'الكمية لكل طلب (طن)',
  qtyPerOrderBags: 'الكمية لكل طلب (أكياس)',
  numberOfOrders: 'عدد الطلبات',
  totalQuantity: 'الإجمالي',
  deliveryOptions: 'خيارات التسليم',
  automatic: 'تلقائي',
  automaticTooltip: 'سيقوم النظام بتعيين الموارد المتاحة تلقائياً للتسليم',
  exworksLabel: 'التوصيل',
  exworksTooltip: 'العميل يستلم من المصنع',
  assignedResource: 'المورد المعين',
  selectResource: 'اختر المورد',
  assignResource: 'تعيين مورد',
  assignTooltip: 'اختر مورداً محدداً يدوياً لهذا التسليم',
  ufcTooltip:
    'ستتولى الشركة المتحدة للأعلاف التسليم باستخدام أسطولها',
  resourcePlaceholder: 'ابحث واختر مورداً...',
  variantUnavailable: 'هذا المنتج غير متوفر في المصنع المحدد',
  bagWeightHint: 'وزن كل كيس {{weight}} كجم',
  quantityMinPlaceholder: 'الحد الأدنى {{value}}',
  quantityMinHint: 'الحد الأدنى: {{value}}',
  quantityMaxHint: 'الحد الأقصى: {{value}}',
  orderQuantityLimitsApplied: 'حدود الطلب: {{min}} - {{max}}',
  orderQuantityMinLimitOnly: 'حدود الطلب: {{min}}',

  // Order summary
  orderSummary: 'ملخص الطلب',
  product: 'المنتج',
  lineTotal: 'إجمالي البند',
  assigned: 'معين',
  grandTotal: 'المجموع الكلي',
  notes: 'ملاحظات',
  notesPlaceholder: 'أضف أي ملاحظات إضافية لهذا الطلب...',
  subCustomerLabel: 'العميل الفرعي',
  subCustomerSelectButton: 'اختر عميلاً فرعياً',
  subCustomerChangeButton: 'تغيير',
  subCustomerClearButton: 'إزالة',
  createdBy: 'أنشئ بواسطة',

  // Credit/Payment
  creditStatus: 'حالة الائتمان',
  agreedCreditLimit: 'حد الائتمان المتفق عليه',
  creditLimitNotLive: 'الرصيد المباشر غير متاح — يتم عرض الحد المتفق عليه.',
  availableCredit: 'الرصيد المتاح',
  orderTotal: 'إجمالي الطلب',
  creditExceededWarning:
    'إجمالي الطلب يتجاوز الرصيد المتاح. يرجى تعديل الطلب أو التواصل مع الدعم.',
  totalCreditLimit: 'إجمالي حد الائتمان',
  vibanNumber: 'رقم VIBAN',
  vibanInfo: 'حول الأموال إلى رقم VIBAN هذا للطلبات المدفوعة مسبقاً',
  savedCards: 'البطاقات المحفوظة',
  addNewCard: 'إضافة بطاقة جديدة',
  newCard: 'بطاقة جديدة',
  useSavedCard: 'استخدام بطاقة محفوظة',
  cardNumber: 'رقم البطاقة',
  expiry: 'تاريخ الانتهاء',
  cvv: 'CVV',
  cardholderName: 'اسم حامل البطاقة',
  cardholderNamePlaceholder: 'الاسم على البطاقة',

  // Detail drawer
  salesOrderDetails: 'تفاصيل طلب المبيعات',
  shipments: 'الشحنات',
  noOrdersForContract: 'لا توجد طلبات لهذا العقد بعد',
  noShipmentsForContract: 'لا توجد شحنات لهذا العقد بعد',
  multipleFactories: 'مصانع متعددة',
  factory: 'المصنع',
  noFactoriesForCity: 'لا توجد مصانع تخدم هذه المدينة',

  // Cancel dialog
  cancelConfirmTitle: 'إلغاء طلب المبيعات',
  cancelConfirmDescription:
    'هل أنت متأكد من إلغاء طلب المبيعات {{name}}؟ لا يمكن التراجع عن هذا الإجراء.',

  // Form section (kept for compatibility)
  form: {
    name: 'اسم طلب المبيعات',
    namePlaceholder: 'أدخل اسم طلب المبيعات',
    status: 'الحالة',
    validation: {
      nameRequired: 'اسم طلب المبيعات مطلوب',
    },
  },

  // Status (kept for compatibility)
  status: {
    draft: 'مسودة',
    active: 'نشط',
    expiring: 'ينتهي قريباً',
    completed: 'مكتمل',
    canceled: 'ملغي',
    inactive: 'غير نشط',
  },

  // Toast messages
  messages: {
    createSuccess: 'تم إنشاء طلب المبيعات بنجاح',
    updateSuccess: 'تم تحديث طلب المبيعات بنجاح',
    deleteSuccess: 'تم حذف طلب المبيعات بنجاح',
    deleteConfirm: 'هل أنت متأكد من حذف طلب المبيعات هذا؟',
  },

  // Table headers
  table: {
    name: 'الاسم',
    status: 'الحالة',
    factory: 'المصنع',
  },

  // Table column labels
  salesOrder: 'أمر البيع',
  products: 'المنتجات',
  period: 'الفترة',
  actions: 'الإجراءات',
  canceled: 'ملغي',
  statusLabel: 'الحالة',

  // Sort options (additional)
  sortByDate: 'تاريخ الإنشاء',
  sortNewest: 'الأحدث أولاً',
  sortOldest: 'الأقدم أولاً',
  sortByOrderNumber: 'رقم الطلب',
  sortByOrderNumberAsc: 'رقم الطلب (أ-ي)',
  sortByOrderNumberDesc: 'رقم الطلب (ي-أ)',
  sortByEndDate: 'تاريخ الانتهاء',
  sortByEndDateAsc: 'تاريخ الانتهاء (الأقرب)',
  sortByEndDateDesc: 'تاريخ الانتهاء (الأبعد)',
  sortByStartDate: 'تاريخ البدء',
  sortByStartDateAsc: 'تاريخ البدء (الأقرب)',
  sortByStartDateDesc: 'تاريخ البدء (الأبعد)',
  sortByValue: 'القيمة',
  sortByValueAsc: 'القيمة (من الأقل)',
  sortByValueDesc: 'القيمة (من الأعلى)',
  sortByConsumption: 'الاستهلاك',
  sortByConsumptionAsc: 'الاستهلاك (من الأقل)',
  sortByConsumptionDesc: 'الاستهلاك (من الأعلى)',
  sortByRemaining: 'المتبقي',
  sortByRemainingAsc: 'المتبقي (من الأقل)',
  sortByRemainingDesc: 'المتبقي (من الأعلى)',
  sortByProductCount: 'عدد المنتجات',
  sortByProductCountAsc: 'المنتجات (الأقل)',
  sortByProductCountDesc: 'المنتجات (الأكثر)',
  sortByStatus: 'الحالة',
  sortByStatusAsc: 'الحالة (أ-ي)',
  sortByStatusDesc: 'الحالة (ي-أ)',
  sortByCreatedAt: 'تاريخ الإنشاء',
  sortByOrdersCount: 'عدد الطلبات',
  sortByOrdersCountAsc: 'عدد الطلبات (الأقل)',
  sortByOrdersCountDesc: 'عدد الطلبات (الأكثر)',
  sortOrderDesc: 'تنازلي',
  sortOrderAsc: 'تصاعدي',

  // Payment method labels
  creditLimit: 'حد الائتمان',
  creditLimitDesc: 'استخدم رصيدك المتاح',
  viban: 'تحويل VIBAN',
  vibanDesc: 'التحويل عبر IBAN الافتراضي',
  creditCard: 'بطاقة ائتمان',
  creditCardDesc: 'الدفع ببطاقة الائتمان',

  // Top-level convenience keys
  name: 'الاسم',
  namePlaceholder: 'أدخل اسم طلب البيع',
  nameRequired: 'اسم طلب البيع مطلوب',
  inactive: 'غير نشط',
  createSuccess: 'تم إنشاء طلب البيع بنجاح',
  updateSuccess: 'تم تحديث طلب البيع بنجاح',
  deleteSuccess: 'تم حذف طلب البيع بنجاح',
  deleteConfirm: 'هل أنت متأكد من حذف طلب البيع هذا؟',
  minValuePlaceholder: '> 0',
  maxValuePlaceholder: 'أي',

  errors: {
    deliveryAddressInvalidRef: 'عنوان التسليم ليس مرجعاً صحيحاً للموقع',
    deliveryAddressNotFound: 'عنوان التسليم لا يشير إلى موقع موجود',
    deliveryAddressTenantMismatch: 'يجب أن ينتمي عنوان التسليم إلى حسابك',
    deliveryAddressValidationFailed: 'تعذّر التحقق من عنوان التسليم',
    cityInvalidRef: 'المدينة ليست مرجعاً صحيحاً',
    cityRequired: 'المدينة مطلوبة',
    cityNotFound: 'المدينة لا تشير إلى مدينة موجودة',
    cityValidationFailed: 'تعذّر التحقق من المدينة',
    cityMismatch: 'يجب أن تتطابق المدينة مع مدينة عنوان التسليم',
    missingDataPayload: 'البيانات مفقودة في الطلب',
    salesOrderLinesRequired: 'يجب إضافة عنصر واحد على الأقل في الطلب',
    guestFieldsRequired: 'طلبات الضيوف تستلزم البريد الإلكتروني ورقم الهاتف',
    salesOrderLinesInvalidFormat: 'يجب أن تكون أسطر الطلب مصفوفة من الكائنات',
    lineCommittedQuantityRequired: 'الكمية المحددة مطلوبة لكل سطر',
    lineVariantRequired: 'نوع المنتج مطلوب لكل سطر',
    lineSplitByRequired: 'طريقة التقسيم مطلوبة لكل سطر',
    documentIdRequired: 'معرف الوثيقة مطلوب',
    notFound: 'طلب البيع غير موجود',
  },

  export: {
    exportingTitle: 'جارٍ التصدير...',
    exportingDesc: 'جلب جميع السجلات',
    exportFailedTitle: 'فشل التصدير',
    exportFailed: 'تعذّر تصدير طلبات البيع',
    emailPromptTitle: 'إلى أين نرسل ملف التصدير؟',
    emailPromptDescription: 'أدخل عنوان البريد الإلكتروني لهذا التصدير. الملفات الصغيرة يتم تنزيلها فورًا، بينما يتم إرسال الملفات الكبيرة عبر البريد الإلكتروني.',
    emailLabel: 'البريد الإلكتروني',
    emailPlaceholder: 'name@example.com',
    emailRequired: 'البريد الإلكتروني مطلوب',
    emailInvalid: 'الرجاء إدخال بريد إلكتروني صالح',
    exportDownloadedTitle: 'تم تنزيل الملف',
    exportDownloadedDescription: 'تم تنزيل {{count}} طلب بيع',
    exportEmailedTitle: 'سيتم إرسال الملف بالبريد',
    exportEmailedDescription: 'سنرسل {{count}} طلب بيع إلى {{email}}',
  },
};
